import { debounce, isArray } from 'lodash';
import { getEditorPointerId } from '@wix/editorx-core-api';

import type {
  AppData,
  CompRef,
  TpaSetStyleParamCallback,
  TpaStyleParamValue,
} from '@wix/document-services-types';
import type { CompPointer } from '@wix/editor-platform-feature-store';
import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { TPAMessage, TPAMessageCallback } from '../types/tpaMessage';

type CompRefToStyleChangesMap = {
  [key: string]: {
    compRef: CompRef;
    callbacks: TpaSetStyleParamCallback[];
    changes: TpaStyleParamValue[];
  };
};

export const createTpaStyleService = (
  platformApiMethods: PlatformApiMethods,
): TpaStyleService => {
  const changesQueue: any[] = [];

  const debouncedUpdateStyle = debounce((isGlobal: boolean) => {
    const tempQueue: any[] = [];

    while (changesQueue.length > 0) {
      tempQueue.push(changesQueue.pop());
    }

    tempQueue.reverse();

    const compRefToStyleChangesMap: CompRefToStyleChangesMap = tempQueue.reduce(
      (
        _compRefToStyleChangesMap,
        { styleToUpdate, callback, compRef, pointerId },
      ) => {
        const changes = _compRefToStyleChangesMap[pointerId]?.changes || [];
        const callbacks = _compRefToStyleChangesMap[pointerId]?.callbacks || [];
        const messageDataAsArray = isArray(styleToUpdate)
          ? styleToUpdate
          : [styleToUpdate];
        const newCallbackAsArray = callback ? [callback] : [];

        return {
          ..._compRefToStyleChangesMap,
          [pointerId]: {
            compRef,
            changes: [...changes, ...messageDataAsArray],
            callbacks: [...callbacks, ...newCallbackAsArray],
          },
        };
      },
      {},
    );

    Object.keys(compRefToStyleChangesMap).forEach((key) => {
      const item = compRefToStyleChangesMap[key];

      platformApiMethods.document.tpa.setStyleParams?.({} as AppData, '', {
        isGlobal,
        // - TODO - refactor TpaStyleParamValue in DM
        styleParams: item.changes,
        compRef: item.compRef,
        callbacks: item.callbacks,
      });

      return platformApiMethods;
    });
  }, 50);

  const setStyleParam = async (
    compRef: CompRef,
    styleToUpdate: TPAMessage['data'],
    callback?: TPAMessageCallback,
    isGlobal?: boolean,
  ) => {
    const pointerId = getEditorPointerId(compRef as CompPointer);

    changesQueue.push({ styleToUpdate, callback, compRef, pointerId });
    debouncedUpdateStyle(!!isGlobal);
  };

  return { setStyleParam };
};

export interface TpaStyleService {
  setStyleParam: (
    compRef: CompRef,
    styleToUpdate: TPAMessage['data'],
    callback: TPAMessageCallback,
    isGlobal?: boolean,
  ) => Promise<void>;
}
